import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Alert,
  MenuItem,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { formatDateTime } from '../lib/format.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

interface SsoConnection {
  protocol: string;
  issuer: string;
  clientId: string;
  hasClientSecret: boolean;
  samlEntityId?: string;
  samlSsoUrl?: string;
  samlSloUrl?: string;
  samlCertificateFingerprints: string[];
  samlEmailAttribute?: string;
  samlNameAttribute?: string;
  samlGroupsAttribute?: string;
  samlSignRequests: boolean;
  domains: string[];
  enforced: boolean;
  jitProvisioning: boolean;
  defaultRole: string;
  enabled: boolean;
  discoveredAt?: string;
  lastLoginAt?: string;
  scimEnabled: boolean;
  scimTokenPrefix?: string;
  scimLastSyncAt?: string;
}
interface GroupMapping {
  externalGroup: string;
  role: string;
}
interface SsoConfig {
  entitled: boolean;
  redirectUri: string;
  samlEntityId: string;
  samlAcsUrl: string;
  samlSloUrl: string;
  samlMetadataUrl: string;
  scimBaseUrl: string;
  groupMappings: GroupMapping[];
  connection: SsoConnection | null;
}

/** The form's own state; `clientSecret` stays empty when one is already stored. */
interface Draft {
  protocol: 'oidc' | 'saml';
  issuer: string;
  clientId: string;
  clientSecret: string;
  samlEntityId: string;
  samlSsoUrl: string;
  samlSloUrl: string;
  /** Pasted certificates; blank keeps what is stored. */
  samlCertificates: string;
  samlEmailAttribute: string;
  samlNameAttribute: string;
  samlGroupsAttribute: string;
  samlSignRequests: boolean;
  domains: string;
  enforced: boolean;
  jitProvisioning: boolean;
  defaultRole: string;
  enabled: boolean;
}

const EMPTY: Draft = {
  protocol: 'oidc',
  issuer: '',
  clientId: '',
  clientSecret: '',
  samlEntityId: '',
  samlSsoUrl: '',
  samlSloUrl: '',
  samlCertificates: '',
  samlEmailAttribute: '',
  samlNameAttribute: '',
  samlGroupsAttribute: '',
  samlSignRequests: false,
  domains: '',
  enforced: false,
  jitProvisioning: true,
  defaultRole: 'MEMBER',
  enabled: false,
};

function toDraft(c: SsoConnection | null): Draft {
  if (!c) return EMPTY;
  return {
    protocol: c.protocol === 'saml' ? 'saml' : 'oidc',
    issuer: c.issuer,
    clientId: c.clientId,
    clientSecret: '',
    samlEntityId: c.samlEntityId ?? '',
    samlSsoUrl: c.samlSsoUrl ?? '',
    samlSloUrl: c.samlSloUrl ?? '',
    samlCertificates: '',
    samlEmailAttribute: c.samlEmailAttribute ?? '',
    samlNameAttribute: c.samlNameAttribute ?? '',
    samlGroupsAttribute: c.samlGroupsAttribute ?? '',
    samlSignRequests: c.samlSignRequests,
    domains: c.domains.join(', '),
    enforced: c.enforced,
    jitProvisioning: c.jitProvisioning,
    defaultRole: c.defaultRole,
    enabled: c.enabled,
  };
}

/**
 * Enterprise single sign-on, configured by the organization's own admin.
 *
 * The screen is written for the person doing an IdP rollout: it shows the
 * redirect URI they must register, it proves the issuer is reachable before
 * they switch anything on, and it says plainly what "enforce" will do to the
 * people who still have passwords.
 */
export function SsoCard({ orgId }: { orgId: string }) {
  const t = useT().sso;
  const qc = useQueryClient();
  const base = `/v1/orgs/${orgId}/security`;
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [error, setError] = useState('');
  const [tested, setTested] = useState<{ ok: boolean; message: string } | null>(null);
  const [groups, setGroups] = useState<GroupMapping[]>([]);
  /** Shown once, right after minting: it is stored only as a hash. */
  const [scimToken, setScimToken] = useState<string | null>(null);

  const cfg = useQuery({
    enabled: !!orgId,
    queryKey: ['security-sso', orgId],
    queryFn: async () => (await api.get(`${base}/sso`)).data as SsoConfig,
  });

  // Load the stored connection into the form once it arrives, and whenever the
  // server sends back a new one after a save.
  useEffect(() => {
    if (cfg.data) {
      setDraft(toDraft(cfg.data.connection));
      setGroups(cfg.data.groupMappings);
    }
  }, [cfg.data]);

  const patchGroup = (i: number, patch: Partial<GroupMapping>) =>
    setGroups((g) => g.map((row, n) => (n === i ? { ...row, ...patch } : row)));
  const removeGroup = (i: number) => setGroups((g) => g.filter((_, n) => n !== i));
  const addGroup = () => setGroups((g) => [...g, { externalGroup: '', role: 'MEMBER' }]);

  const saveGroups = useMutation({
    mutationFn: async (rows: GroupMapping[]) =>
      (await api.put(`${base}/sso/groups`, rows.filter((r) => r.externalGroup.trim()))).data as SsoConfig,
    onSuccess: (data) => qc.setQueryData(['security-sso', orgId], data),
  });

  const setScim = useMutation({
    mutationFn: async (enabled: boolean) =>
      (await api.put(`${base}/sso/scim`, { enabled })).data as SsoConfig,
    onSuccess: (data) => {
      setScimToken(null);
      qc.setQueryData(['security-sso', orgId], data);
    },
  });

  const mintScim = useMutation({
    mutationFn: async () => (await api.post(`${base}/sso/scim-token`)).data as { token: string },
    onSuccess: (d) => {
      setScimToken(d.token);
      void qc.invalidateQueries({ queryKey: ['security-sso', orgId] });
    },
  });

  const save = useMutation({
    mutationFn: async (d: Draft) =>
      (
        await api.put(`${base}/sso`, {
          protocol: d.protocol,
          ...(d.protocol === 'oidc'
            ? {
                issuer: d.issuer,
                clientId: d.clientId,
                ...(d.clientSecret ? { clientSecret: d.clientSecret } : {}),
              }
            : {
                samlEntityId: d.samlEntityId,
                samlSsoUrl: d.samlSsoUrl,
                samlSloUrl: d.samlSloUrl,
                // Blank keeps the stored certificates, the same contract the
                // OIDC client secret has.
                ...(d.samlCertificates.trim() ? { samlCertificates: d.samlCertificates } : {}),
                samlEmailAttribute: d.samlEmailAttribute,
                samlNameAttribute: d.samlNameAttribute,
                samlGroupsAttribute: d.samlGroupsAttribute,
                samlSignRequests: d.samlSignRequests,
              }),
          domains: d.domains,
          enforced: d.enforced,
          jitProvisioning: d.jitProvisioning,
          defaultRole: d.defaultRole,
          enabled: d.enabled,
        })
      ).data as SsoConfig,
    onSuccess: (data) => {
      setError('');
      qc.setQueryData(['security-sso', orgId], data);
    },
    onError: (e: { response?: { data?: { error?: { message?: string } } } }) =>
      setError(e.response?.data?.error?.message ?? t.saveFailed),
  });

  const remove = useMutation({
    mutationFn: async () => (await api.delete(`${base}/sso`)).data as SsoConfig,
    onSuccess: (data) => {
      setError('');
      setDraft(EMPTY);
      qc.setQueryData(['security-sso', orgId], data);
    },
  });

  const test = useMutation({
    mutationFn: async (issuer: string) =>
      (await api.post(`${base}/sso/test`, { issuer })).data as {
        ok: boolean;
        issuer?: string;
        error?: string;
      },
    onSuccess: (r) =>
      setTested(
        r.ok
          ? { ok: true, message: t.reached(r.issuer ?? '') }
          : { ok: false, message: r.error ?? t.unreachable },
      ),
  });

  const entitled = cfg.data?.entitled ?? false;
  const conn = cfg.data?.connection ?? null;
  const locked = !entitled || save.isPending;

  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <BadgeIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700}>
          {t.title}
        </Typography>
        <Chip size="small" color="secondary" variant="outlined" label={t.enterprise} />
        {conn?.enabled && <Chip size="small" color="success" label={t.active} />}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        {t.intro}
      </Typography>

      {!entitled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t.gate}
        </Alert>
      )}

      <Alert severity="info" icon={false} sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {t.redirectLabel}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
            {cfg.data?.redirectUri ?? '—'}
          </Typography>
          <Tooltip title={t.copy}>
            <IconButton
              size="small"
              onClick={() => void navigator.clipboard?.writeText(cfg.data?.redirectUri ?? '')}
            >
              <ContentCopyIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {tested && (
        <Alert severity={tested.ok ? 'success' : 'error'} sx={{ mb: 2 }} onClose={() => setTested(null)}>
          {tested.message}
        </Alert>
      )}

      <Stack spacing={2} sx={{ maxWidth: 760 }}>
        {/* Protocol first: it decides which half of this form matters. */}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={draft.protocol}
          disabled={locked}
          onChange={(_, v) => v && setDraft({ ...draft, protocol: v as 'oidc' | 'saml' })}
        >
          <ToggleButton value="oidc">{t.oidc}</ToggleButton>
          <ToggleButton value="saml">{t.saml}</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          {draft.protocol === 'saml' ? t.samlHint : t.oidcHint}
        </Typography>

        {draft.protocol === 'oidc' ? (
          <>
            <TextField
              size="small"
              label={t.issuer}
              placeholder={t.issuerPlaceholder}
              helperText={t.issuerHelp}
              value={draft.issuer}
              disabled={locked}
              onChange={(e) => setDraft({ ...draft, issuer: e.target.value })}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                size="small"
                fullWidth
                label={t.clientId}
                value={draft.clientId}
                disabled={locked}
                onChange={(e) => setDraft({ ...draft, clientId: e.target.value })}
              />
              <TextField
                size="small"
                fullWidth
                type="password"
                label={t.clientSecret}
                placeholder={conn?.hasClientSecret ? t.secretStored : ''}
                helperText={conn?.hasClientSecret ? t.secretStoredHelp : ''}
                value={draft.clientSecret}
                disabled={locked}
                onChange={(e) => setDraft({ ...draft, clientSecret: e.target.value })}
              />
            </Stack>
          </>
        ) : (
          <>
            {/* What the IdP administrator needs from us, before anything else. */}
            <Alert severity="info" icon={false}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {t.samlSp}
              </Typography>
              <CopyLine label={t.samlEntityId} value={cfg.data?.samlEntityId ?? ''} copyHint={t.copy} />
              <CopyLine label={t.samlAcs} value={cfg.data?.samlAcsUrl ?? ''} copyHint={t.copy} />
              <CopyLine label={t.samlSpSlo} value={cfg.data?.samlSloUrl ?? ''} copyHint={t.copy} />
              {conn?.protocol === 'saml' && (
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                  <a href={`${base}/sso/metadata`} target="_blank" rel="noreferrer">
                    {t.samlMetadata}
                  </a>
                </Typography>
              )}
            </Alert>

            <TextField
              size="small"
              label={t.samlIdpEntityId}
              helperText={t.samlIdpEntityIdHint}
              value={draft.samlEntityId}
              disabled={locked}
              onChange={(e) => setDraft({ ...draft, samlEntityId: e.target.value })}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                size="small"
                fullWidth
                label={t.samlSsoUrl}
                value={draft.samlSsoUrl}
                disabled={locked}
                onChange={(e) => setDraft({ ...draft, samlSsoUrl: e.target.value })}
              />
              <TextField
                size="small"
                fullWidth
                label={t.samlSloUrl}
                value={draft.samlSloUrl}
                disabled={locked}
                onChange={(e) => setDraft({ ...draft, samlSloUrl: e.target.value })}
              />
            </Stack>
            <TextField
              size="small"
              multiline
              minRows={3}
              label={t.samlCertificates}
              helperText={
                conn?.samlCertificateFingerprints.length
                  ? `${t.samlStored(conn.samlCertificateFingerprints.length)}, ${t.samlCertificatesHint}`
                  : t.samlCertificatesHint
              }
              value={draft.samlCertificates}
              disabled={locked}
              onChange={(e) => setDraft({ ...draft, samlCertificates: e.target.value })}
              sx={{ '& textarea': { fontFamily: 'ui-monospace, monospace', fontSize: 12 } }}
            />
            {!!conn?.samlCertificateFingerprints.length && (
              <Stack spacing={0.25}>
                {conn.samlCertificateFingerprints.map((f) => (
                  <Typography
                    key={f}
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}
                  >
                    {f}
                  </Typography>
                ))}
              </Stack>
            )}

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">{t.samlAttributes}</Typography>
            </Divider>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              {t.samlAttributesHint}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                size="small"
                fullWidth
                label={t.samlEmailAttribute}
                value={draft.samlEmailAttribute}
                disabled={locked}
                onChange={(e) => setDraft({ ...draft, samlEmailAttribute: e.target.value })}
              />
              <TextField
                size="small"
                fullWidth
                label={t.samlNameAttribute}
                value={draft.samlNameAttribute}
                disabled={locked}
                onChange={(e) => setDraft({ ...draft, samlNameAttribute: e.target.value })}
              />
              <TextField
                size="small"
                fullWidth
                label={t.samlGroupsAttribute}
                value={draft.samlGroupsAttribute}
                disabled={locked}
                onChange={(e) => setDraft({ ...draft, samlGroupsAttribute: e.target.value })}
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.samlSignRequests}
                  disabled={locked}
                  onChange={(e) => setDraft({ ...draft, samlSignRequests: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>{t.samlSignRequests}</Typography>
                  <Typography variant="caption" color="text.secondary">{t.samlSignRequestsHint}</Typography>
                </Box>
              }
            />
          </>
        )}

        <TextField
          size="small"
          label={t.domains}
          placeholder={t.domainsPlaceholder}
          helperText={t.domainsHelp}
          value={draft.domains}
          disabled={locked}
          onChange={(e) => setDraft({ ...draft, domains: e.target.value })}
        />

        <Divider />

        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {t.jit}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t.jitBody}
            </Typography>
          </Box>
          <Switch
            checked={draft.jitProvisioning}
            disabled={locked}
            onChange={(e) => setDraft({ ...draft, jitProvisioning: e.target.checked })}
          />
        </Stack>

        <TextField
          select
          size="small"
          label={t.defaultRole}
          sx={{ maxWidth: 260 }}
          value={draft.defaultRole}
          disabled={locked || !draft.jitProvisioning}
          onChange={(e) => setDraft({ ...draft, defaultRole: e.target.value })}
        >
          <MenuItem value="VIEWER">{t.roles.VIEWER}</MenuItem>
          <MenuItem value="MEMBER">{t.roles.MEMBER}</MenuItem>
          <MenuItem value="ADMIN">{t.roles.ADMIN}</MenuItem>
        </TextField>

        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {t.enforce}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t.enforceBody}
            </Typography>
          </Box>
          <Switch
            checked={draft.enforced}
            disabled={locked}
            onChange={(e) => setDraft({ ...draft, enforced: e.target.checked })}
          />
        </Stack>

        <FormControlLabel
          control={
            <Switch
              checked={draft.enabled}
              disabled={locked}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            />
          }
          label={<Typography variant="body2" fontWeight={600}>{t.enabled}</Typography>}
        />

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            size="small"
            disabled={locked}
            onClick={() => save.mutate(draft)}
          >
            {save.isPending ? t.saving : t.save}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!draft.issuer || test.isPending}
            onClick={() => test.mutate(draft.issuer)}
          >
            {test.isPending ? t.testing : t.testProvider}
          </Button>
          {conn && (
            <Button color="error" size="small" disabled={!entitled} onClick={() => remove.mutate()}>
              {t.remove}
            </Button>
          )}
        </Stack>

        <Divider sx={{ my: 1 }} />

        {/* Directory groups → roles. Shared by both protocols and by SCIM. */}
        <Box>
          <Typography variant="body2" fontWeight={600}>{t.groups}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t.groupsHint}
          </Typography>
          <Stack spacing={1}>
            {groups.map((g, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  fullWidth
                  label={t.groupName}
                  value={g.externalGroup}
                  disabled={!entitled}
                  onChange={(e) => patchGroup(i, { externalGroup: e.target.value })}
                />
                <TextField
                  select
                  size="small"
                  label={t.groupRole}
                  sx={{ minWidth: 160 }}
                  value={g.role}
                  disabled={!entitled}
                  onChange={(e) => patchGroup(i, { role: e.target.value })}
                >
                  <MenuItem value="VIEWER">{t.roles.VIEWER}</MenuItem>
                  <MenuItem value="MEMBER">{t.roles.MEMBER}</MenuItem>
                  <MenuItem value="ADMIN">{t.roles.ADMIN}</MenuItem>
                </TextField>
                <IconButton size="small" disabled={!entitled} onClick={() => removeGroup(i)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" disabled={!entitled} onClick={addGroup}>
              {t.addGroup}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!entitled || saveGroups.isPending}
              onClick={() => saveGroups.mutate(groups)}
            >
              {t.saveGroups}
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* SCIM. Only useful once a connection exists, so it says so. */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" fontWeight={600}>{t.scim}</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Switch
              checked={conn?.scimEnabled ?? false}
              disabled={!entitled || !conn || setScim.isPending}
              onChange={(e) => setScim.mutate(e.target.checked)}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t.scimHint}
          </Typography>

          {conn?.scimEnabled && (
            <Stack spacing={1}>
              <CopyLine label={t.scimUrl} value={cfg.data?.scimBaseUrl ?? ''} copyHint={t.copy} />
              {scimToken ? (
                <Alert severity="success" onClose={() => setScimToken(null)}>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    {t.scimTokenOnce}
                  </Typography>
                  <CopyLine label={t.scimToken} value={scimToken} copyHint={t.copy} />
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {conn.scimTokenPrefix ? t.scimActive(conn.scimTokenPrefix) : t.scimNoToken}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {conn.scimLastSyncAt ? t.scimLastSync(formatDateTime(conn.scimLastSyncAt)) : t.scimNeverSynced}
              </Typography>
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={mintScim.isPending}
                  onClick={() => mintScim.mutate()}
                >
                  {conn.scimTokenPrefix ? t.scimRegenerate : t.scimGenerate}
                </Button>
              </Box>
            </Stack>
          )}
        </Box>

        {conn && (
          <Typography variant="caption" color="text.secondary">
            {conn.lastLoginAt ? t.lastLogin(formatDateTime(conn.lastLoginAt)) : t.noLogin}
            {conn.discoveredAt && t.discovered(formatDateTime(conn.discoveredAt))}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

/** A label, a monospace value and a copy button — the shape an administrator
 *  is pasting from into their identity provider. */
function CopyLine({ label, value, copyHint }: { label: string; value: string; copyHint: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 150, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, wordBreak: 'break-all', flexGrow: 1 }}
      >
        {value || '—'}
      </Typography>
      <Tooltip title={copyHint}>
        <IconButton
          size="small"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch {
              // Clipboard blocked; the value is selectable on screen.
            }
          }}
        >
          {copied ? <CheckIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
